import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useStore } from '@tanstack/react-form'
import { createFileRoute } from '@tanstack/react-router'
import clsx from 'clsx'
import { useAppForm, FormField, TextInput, SelectInput } from '@tandem/ui/form'
import $type from '@tandem/ui/type.css'
import $table from '@tandem/ui/table.css'
import Spinner from '@tandem/ui/Spinner'
import { useTRPC } from '@/trpc'
import { useAuth } from '@/auth/AuthContext'
import CounterpartyModal from '../CounterpartyModal'
import { fmt } from '../types'
import $ from './-Counterparties.css'
import $layout from '@tandem/ui/layout.css'

export const Route = createFileRoute('/proexpert/')({
  component: CounterpartiesPage,
})

let SHORT: Record<string, string> = { sez: 'СЭЗ', iii: 'ИИИ', licrm: 'РПН', egrul: 'ЕГРЮЛ' }

function CounterpartiesPage() {
  let { company } = useAuth()
  let trpc = useTRPC()
  let enabled = company?.features?.includes('proexpert') ?? false

  let { data: meta = [] } = useQuery({ ...trpc.proexpert.registries.queryOptions(), enabled, staleTime: Infinity })
  let { data: rows = [], isFetching } = useQuery({ ...trpc.proexpert.counterparty.list.queryOptions(), enabled })
  let { data: status } = useQuery({ ...trpc.proexpert.status.queryOptions(), enabled, refetchInterval: 5000 })

  let form = useAppForm({
    defaultValues: {
      search: '',
      registry: '' as string | undefined,
    },
  })
  let search = useStore(form.store, (s) => s.values.search)
  let registry = useStore(form.store, (s) => s.values.registry)

  let [selected, setSelected] = useState<string | null>(null)

  let filtered = useMemo(() => {
    let q = search.trim().toLowerCase()
    return rows
      .filter((r) => !q || r.inn.includes(q) || (r.company_name ?? '').toLowerCase().includes(q))
      .filter((r) => !registry || (r.registries[registry] ?? 0) > 0)
      .sort((a, b) => (a.company_name ?? '').localeCompare(b.company_name ?? '', 'ru'))
  }, [rows, search, registry])

  if (!enabled) return <p class={$type.muted}>Раздел недоступен.</p>

  return (
    <div class={$.page}>

      <div class={clsx($layout.container, $layout.center)}>
        <h3>Контрагенты</h3>

        <div class={$.filters}>
          <form.Field name='search'>
            {(field) => (
              <FormField field={field} label='Поиск по названию или ИНН'>
                <TextInput field={field} />
              </FormField>
            )}
          </form.Field>
          <form.Field name='registry'>
            {(field) => (
              <FormField field={field} label='Реестр'>
                <SelectInput
                  field={field}
                  options={meta.map((m) => ({ value: m.label, label: m.name }))}
                />
              </FormField>
            )}
          </form.Field>
        </div>

        <span class={clsx($type.small, $type.muted)}>
          {status?.progress?.state === 'running' ?
            `Обрабатывается: ${status.progress.inn}${status.progress.name ? ` — ${status.progress.name}` : ''} (${status.progress.i}/${status.progress.total})`
          : status?.last_sync_at ?
            `Синхронизировано: ${fmt(status.last_sync_at)}`
          : null}
        </span>
      </div>

      {filtered.length > 0 ? (
        <div class={$.tableScroller}>
        <table class={clsx($.table, $table.table, $table.condensed, $type.small)}>
          <thead>
            <tr>
              <th>Контрагент</th>
              <th>ИНН</th>
              {meta.map((m) => (
                <th key={m.label} title={m.name} class='text-center'>{SHORT[m.label] ?? m.label.toUpperCase()}</th>
              ))}
              <th>Синхронизирован</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((r) => (
              <tr
                key={r.inn}
                class={clsx($.row, r.inn === selected && $.selected)}
                onClick={() => setSelected(r.inn === selected ? null : r.inn)}
              >
                <td>{r.company_name ?? '—'}</td>
                <td>{r.inn}</td>
                {meta.map((m) => {
                  let found = r.registries[m.label]
                  return (
                    <td key={m.label} class='text-center'>
                      {found == null ? null : found || ''}
                    </td>
                  )
                })}
                <td>{fmt(r.synced_at ?? r.updated_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      ) : (
        <p class={$type.muted} style={{ marginTop: '1em' }}>
          {isFetching ? <Spinner /> : rows.length > 0 ? 'Ничего не найдено' : 'Нет контрагентов'}
        </p>
      )}

      {selected && <CounterpartyModal inn={selected} meta={meta} onClose={() => setSelected(null)} />}
    </div>
  )
}
